import React, { FormEvent, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from "lucide-react";
import { siteLinks } from "../../data/site";
type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  improvements: string[];
  experience: string;
};
const initialData: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  improvements: [],
  experience: ""
};
const improvementOptions = ["Strength", "Mobility", "Performance", "Movement quality", "Consistency", "Body composition", "General fitness", "Other"];
export function AssessmentForm() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<FormData>(initialData);
  const totalSteps = 3;
  function updateField<K extends keyof FormData>(field: K, value: FormData[K]) {
    setData(current => ({
      ...current,
      [field]: value
    }));
  }
  function toggleImprovement(value: string) {
    updateField("improvements", data.improvements.includes(value) ? data.improvements.filter(item => item !== value) : [...data.improvements, value]);
  }
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step < totalSteps - 1) {
      setStep(current => current + 1);
      return;
    }
    window.location.assign(siteLinks.coachRxCheckout);
  }
  return <form onSubmit={handleSubmit} className="bg-cream text-ink" id="assessment-form" data-id="element-4" data-mp="zp59ug6k01fy">
      <div className="flex items-center justify-between border-b border-ink/15 px-6 py-5 sm:px-10" data-id="element-5" data-mp="1emnylij8eene"><p className="text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-6" data-mp="19nuhlg4qk5ih">Start your assessment</p><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink/45" data-id="element-7" data-mp="19m058q7j5lq0">{step + 1} / {totalSteps}</p></div>
      <div className="h-0.5 bg-ink/10" data-id="element-8" data-mp="1jdw96hwas625"><div className="h-full bg-bronze transition-[width] duration-300 ease-out" style={{
        width: `${(step + 1) / totalSteps * 100}%`
      }} data-id="element-9" data-mp="1jep8ixsxfta9" /></div>
      <div className="min-h-[460px] p-6 sm:p-10 lg:p-14" data-id="element-10" data-mp="1xb2kjreoz2hc">
        {step === 0 && <PersonalStep data={data} updateField={updateField} data-id="element-11" data-mp="1sg5g6xscxhas" />}
        {step === 1 && <ImprovementStep selected={data.improvements} toggle={toggleImprovement} data-id="element-12" data-mp="q27kurmxmij9" />}
        {step === 2 && <ExperienceStep value={data.experience} onChange={value => updateField("experience", value)} data-id="element-13" data-mp="u3b0bd12icjy" />}
      </div>
      <div className="flex items-center justify-between border-t border-ink/15 px-6 py-5 sm:px-10" data-id="element-14" data-mp="z01c3o5ualzk"><button type="button" onClick={() => setStep(current => Math.max(0, current - 1))} className={`inline-flex min-h-11 items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] ${step === 0 ? "invisible" : ""}`} data-id="element-15" data-mp="13tvveqhj8yy4"><ArrowLeftIcon className="h-4 w-4" data-id="element-16" data-mp="1donwi4abq3xn" /> Back</button><button type="submit" className="inline-flex min-h-12 items-center gap-3 bg-bronze px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-[background-color,color,transform] duration-200 ease-out hover:bg-sand hover:text-ink active:translate-y-px" data-id="element-17" data-mp="1hru8lsk4apc1">{step === totalSteps - 1 ? "Continue to assessment" : "Continue"}<ArrowRightIcon className="h-4 w-4" data-id="element-18" data-mp="3oj20wb6nj4oe" /></button></div>
      <p className="px-6 pb-6 text-[10px] leading-5 text-ink/45 sm:px-10" data-id="element-19" data-mp="3gzj2oxou74v8">The final step opens the external CoachRx checkout. Scheduling becomes available only after payment.</p>
    </form>;
}
type UpdateField = <K extends keyof FormData>(field: K, value: FormData[K]) => void;
function PersonalStep({
  data,
  updateField
}: {
  data: FormData;
  updateField: UpdateField;
}) {
  const fields: {
    key: "firstName" | "lastName" | "email";
    label: string;
    type: string;
  }[] = [{
    key: "firstName",
    label: "First name",
    type: "text"
  }, {
    key: "lastName",
    label: "Last name",
    type: "text"
  }, {
    key: "email",
    label: "Email",
    type: "email"
  }];
  return <div data-id="element-20" data-mp="3pxw60uapr2hn"><StepHeading title="Tell me about you." data-id="element-21" data-mp="3q1s9pdkwcg3v" /><div className="mt-9 grid gap-x-5 gap-y-6 sm:grid-cols-2" data-id="element-22" data-mp="3lynabjw44ujg">{fields.map(field => <label key={field.key} className={field.key === "email" ? "sm:col-span-2" : ""} data-id="element-23" data-mp="2ekk78xtt9a2c"><span className="text-[10px] font-bold uppercase tracking-[0.15em] text-ink/55" data-id="element-24" data-mp="2eift7n276uoc">{field.label}</span><input required type={field.type} value={data[field.key]} onChange={event => updateField(field.key, event.target.value)} className="mt-3 w-full border-0 border-b border-ink/30 bg-transparent px-0 py-3 text-base outline-none transition-colors duration-200 ease-out focus:border-bronze focus:ring-0" data-id="element-25" data-mp="2al1ni5dq7513" /></label>)}</div></div>;
}
function ImprovementStep({
  selected,
  toggle
}: {
  selected: string[];
  toggle: (value: string) => void;
}) {
  return <div data-id="element-26" data-mp="l0gw06691ita"><StepHeading title="What would you like to improve?" hint="Choose as many as apply." data-id="element-27" data-mp="l0gx69hzjouv" /><div className="mt-9 grid gap-3 sm:grid-cols-2" data-id="element-28" data-mp="q0d1k9ulhiay">{improvementOptions.map(option => {
        const active = selected.includes(option);
        return <button type="button" key={option} onClick={() => toggle(option)} aria-pressed={active} className={`min-h-14 border px-5 py-4 text-left text-sm font-semibold transition-[background-color,color,border-color] duration-200 ease-out ${active ? "border-bronze bg-bronze text-cream" : "border-ink/20 hover:border-bronze"}`} data-id="element-29" data-mp="bzafhx3lo801">{option}{active ? <CheckIcon className="ml-2 inline h-4 w-4" data-id="element-30" data-mp="30pjnz3po4fa4" /> : null}</button>;
      })}</div></div>;
}
function ExperienceStep({
  value,
  onChange
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return <div data-id="element-31" data-mp="2obd9gen06rdg"><StepHeading title="What are you experiencing?" hint="Briefly tell me what you would like help with." data-id="element-32" data-mp="2obmwpugnykfg" /><label className="mt-9 block" data-id="element-33" data-mp="3346xwpk96hu9"><span className="sr-only" data-id="element-34" data-mp="2y6x30ue3hdz1">What you are experiencing</span><textarea required value={value} onChange={event => onChange(event.target.value)} placeholder="A short description is enough..." rows={6} className="w-full resize-none border border-ink/20 bg-stone/40 p-5 text-base leading-7 outline-none placeholder:text-ink/35 focus:border-bronze focus:ring-0" data-id="element-35" data-mp="2y5di2qhhueqx" /></label></div>;
}
function StepHeading({
  title,
  hint
}: {
  title: string;
  hint?: string;
}) {
  return <div data-id="element-36" data-mp="2fevuf0biq9cf"><h3 className="max-w-2xl font-display text-4xl font-semibold uppercase leading-[0.92] tracking-[-0.035em] sm:text-6xl" data-id="element-37" data-mp="2ff47otbjzgo5">{title}</h3>{hint ? <p className="mt-4 text-sm leading-7 text-ink/55" data-id="element-38" data-mp="1mt2z1bsjxwzo">{hint}</p> : null}</div>;
}